import {
  type FetchedProcedureResult,
  fetchProcedure,
  type Procedure,
  type ProcedureFetchParams,
} from '@epihub/core/src/procedure';
import { type DeepPartial, type HasNoRequiredProperties, type RemainingPayload } from '@epihub/core/src/ts';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import type { z } from 'zod/v4';
import { useMemo } from 'react';
import { _get, deepMergeBasic } from '@epihub/core/src/fn/object';
import { useLatestRef } from '@epihub/web/src/hooks/useLatestRef';
import { buildProcedureKey } from './buildProcedureKey';
import type { DeferrablePayload, ReactProcedureContext } from './types';

type MutationOptionsBase<Def extends Procedure> = {
  onSuccess?: (
    data: FetchedProcedureResult<Def>,
    variables: any,
    context: any,
  ) => Promise<void | FetchedProcedureResult<Def>>;
};

type MutationOptions<Def extends Procedure> = MutationOptionsBase<Def> & {
  request?: DeepPartial<ProcedureFetchParams<Def>>;
};

type MutationOptionsV2<Def extends Procedure, RequestPayload extends DeepPartial<ProcedureFetchParams<Def>>> =
  & MutationOptionsBase<Def>
  & {
    request?: RequestPayload;
  };

type MutationCallOptions<Def extends Procedure> = {
  request?: DeepPartial<ProcedureFetchParams<Def>>;
};

type MutationCallOptionsV2<Def extends Procedure, RequestPayload extends DeepPartial<ProcedureFetchParams<Def>>> = {
  request?: RemainingPayload<ProcedureFetchParams<Def>, RequestPayload>;
};

export function useMutationProcedure<Def extends Procedure>(
  procedure: Def,
  _context: DeferrablePayload<ReactProcedureContext<z.infer<Def['headers']>>>,
  _options?: DeferrablePayload<MutationOptions<Def>>,
) {
  const procedureRef = useLatestRef(procedure);
  const context = typeof _context === 'function' ? _context() : _context;
  const options = typeof _options === 'function' ? _options() : _options;

  const onSuccessRef = useLatestRef(options?.onSuccess);

  const payload = useMemo(() => {
    return deepMergeBasic(options?.request ?? {}, {
      query: {},
      params: {},
      body: {},
    });
  }, [options?.request]);

  const pathSegments = useMemo(() => {
    return buildProcedureKey(procedureRef.current, payload as any);
  }, [procedureRef, payload]);

  return useMutation({
    mutationKey: pathSegments,
    // @ts-expect-error
    mutationFn: async (
      ...p: HasNoRequiredProperties<ProcedureFetchParams<Def>> extends true ? [p1?: MutationCallOptions<Def>]
        : [p1: MutationCallOptions<Def>]
    ) => {
      const fullPayload = deepMergeBasic({
        query: _get(payload, 'query', {}),
        params: _get(payload, 'params', {}),
        body: _get(payload, 'body', {}),
      }, p?.[0]?.request ?? {});

      const result = await fetchProcedure(procedureRef.current, {
        ...fullPayload as any,
        baseURL: context.baseURL,
        headers: context.headers,
        credentials: context.credentials,
      });

      const next = await onSuccessRef.current?.(result, options, context);
      return next ?? result;
    },
  });
}

export function buildUseMutationProcedure(useContext: () => ReactProcedureContext) {
  /**
   * Overload 1: When no options provided, use full ProcedureFetchParams
   */
  function _useMutationProcedure<Def extends Procedure>(
    procedure: Def,
    options?: undefined,
  ): UseMutationResult<
    FetchedProcedureResult<Def>,
    Error,
    { request?: ProcedureFetchParams<Def> }
  >;

  /**
   * Overload 1: When options provided, use RemainingPayload
   */
  function _useMutationProcedure<
    Def extends Procedure,
    RequestPayload extends DeepPartial<ProcedureFetchParams<Def>>,
  >(
    procedure: Def,
    options: DeferrablePayload<MutationOptionsV2<Def, RequestPayload>>,
  ): UseMutationResult<
    FetchedProcedureResult<Def>,
    Error,
    MutationCallOptionsV2<Def, RequestPayload>
  >;

  function _useMutationProcedure<
    Def extends Procedure,
    RequestPayload extends DeepPartial<ProcedureFetchParams<Def>>,
  >(
    procedure: Def,
    options?: DeferrablePayload<MutationOptionsV2<Def, RequestPayload>>,
  ): any {
    const context = useContext();
    // @ts-expect-error
    return useMutationProcedure(procedure, context, options);
  }
  return _useMutationProcedure;
}
